import { matchPath } from 'react-router-dom';
import { AddIconOutlined } from '@m/alchemy-ui/icons/AddIconOutlined';
import { AppMenuListItem } from '@m/alchemy-ui/AppMenuListItem';
import { AppMenuList } from '@m/alchemy-ui/AppMenuList';
import { AppMenuListExpandableItem } from '@m/alchemy-ui/AppMenuListExpandableItem';
import { BulletIcon } from '@m/alchemy-ui/icons/BulletIcon';

import { useSelectorTyped } from 'common/store/useSelectorTyped';
import { qaLinks } from 'common/constants/menuItems';
import { toValidatedList } from 'common/utils/rbacHelpers';
import { useRBACSettings } from 'common/hooks/useRBACSettings';

import { NewQaReviewButton } from './Nav.styled';
import AppNavListItemTertiaryWithoutIcon from './AppNavListItemTertiaryWithoutIcon';
import MenuItem from './MenuItem';
import { NavBarItem } from './types';
import styled from 'styled-components';

interface QAProps {
  isManageQA: boolean;
  pathname: string;
  isMobileMenu?: boolean;
  hasChangedAssignments?: boolean;
}

const MenuItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const QA = ({
  isManageQA,
  pathname,
  isMobileMenu,
  hasChangedAssignments,
}: QAProps) => {
  const qaStandalone = useSelectorTyped(state => state.company.qaStandalone);
  const permissions = useSelectorTyped(state => state.permissions);
  const { hasRBACEnabled } = useRBACSettings();

  let qaMenuLinks: NavBarItem[] = [];

  if (hasRBACEnabled) {
    qaMenuLinks = [
      ...toValidatedList(
        permissions.viewQADashboard,
        qaLinks.dashboardOverview
      ),
      ...toValidatedList(permissions.reviewQA, qaLinks.interactions),
      ...toValidatedList(
        permissions.reviewQA || permissions.manageCalibrations,
        qaLinks.calibrations
      ),
      ...toValidatedList(permissions.viewAssignments, {
        ...qaLinks.assignments,
        showIndicator: hasChangedAssignments,
      }),
      ...toValidatedList(permissions.viewReviewers, qaLinks.reviewers),
    ];
  } else {
    qaMenuLinks = [
      ...(isManageQA
        ? [
            qaLinks.dashboardOverview,
            qaLinks.interactions,
            qaLinks.calibrations,
            {
              ...qaLinks.assignments,
              showIndicator: hasChangedAssignments,
            },
          ]
        : []),
      ...(permissions.viewReviewers ? [qaLinks.reviewers] : []),
    ];
  }

  const isQaActive = qaMenuLinks.some(link =>
    matchPath(pathname, {
      path: link.url,
    })
  );

  const renderNewReviewButton = () => {
    if (!permissions[hasRBACEnabled ? 'reviewQA' : 'manageQa']) return null;
    return (
      <NewQaReviewButton
        className="margin-horizontal-medium text-white"
        icon={<AddIconOutlined aria-hidden aria-label="" />}
        href={qaLinks.newReview.url}
      >
        {qaLinks.newReview.label}
      </NewQaReviewButton>
    );
  };

  const getMenuListItems = () => {
    const isNewQAReviewButtonVisible = hasRBACEnabled
      ? permissions.reviewQA
      : isManageQA;

    return (
      <>
        {qaMenuLinks.map(({ url, key, label, showIndicator }) => {
          const isItemActive = Boolean(
            matchPath(pathname, {
              path: url,
            })
          );

          return (
            <AppMenuListItem key={key} isActive={isItemActive}>
              <MenuItemWrapper>
                <a href={url}>{label}</a>
                {showIndicator && (
                  <BulletIcon
                    size={9}
                    aria-label="notification"
                    color="base"
                    palette="illustrativeOrange"
                  />
                )}
              </MenuItemWrapper>
            </AppMenuListItem>
          );
        })}
        {isNewQAReviewButtonVisible && (
          <div className="flex justify-center padding-vertical-medium border-lightish-gray-top">
            <NewQaReviewButton
              className="new-qa-review width-100 margin-horizontal-medium"
              icon={<AddIconOutlined aria-hidden aria-label="" />}
              href={qaLinks.newReview.url}
            >
              {qaLinks.newReview.label}
            </NewQaReviewButton>
          </div>
        )}
      </>
    );
  };

  if (qaStandalone) {
    return (
      <>
        {qaMenuLinks.map(menuLink => {
          const isActive = Boolean(
            matchPath(pathname, {
              path: menuLink.url,
            })
          );

          return (
            <MenuItem
              key={menuLink.key}
              label={menuLink.label}
              url={menuLink.url}
              keyName={menuLink.key}
              icon={menuLink.icon}
              isActive={isActive}
              isMobileMenu={isMobileMenu}
            />
          );
        })}
        {renderNewReviewButton()}
      </>
    );
  }

  return isMobileMenu ? (
    <AppMenuListExpandableItem label="QA">
      {/* @ts-expect-error */}
      <AppMenuList className="padding-left-medium">
        {getMenuListItems()}
      </AppMenuList>
    </AppMenuListExpandableItem>
  ) : (
    <AppNavListItemTertiaryWithoutIcon
      priority="primary"
      isActive={isQaActive}
      label="QA"
      overlay={() => {
        return (
          // AppMenuList accepts a className prop but it is not present in its interface.
          // @ts-expect-error
          <AppMenuList className="padding-none margin-bottom-none">
            {getMenuListItems()}
          </AppMenuList>
        );
      }}
    />
  );
};

export default QA;
